import clsx from "clsx";
import type { Feature, FeatureCollection } from "geojson";
import { type ComponentType, useMemo } from "react";

import type { LonLat } from "../../api/types";
import { motion } from "../../components/presence";
import { t } from "../../i18n/i18n";
import { revealPaint, useFadeIn } from "../reveal";
import { FRIEND_BLUE } from "../units/symbols";
import { axisTagText } from "../zones/format";
import { corridorWidthM, isZoneLevel, orientedRing, preparedFootprint } from "../zones/footprint";

/** Terrain libre d'un ordre hors des camps sur la carte d'aperçu de la vérification (docs/20 §14), avec le rendu des
 *  tracés du terrain libre de la vue Manœuvre (`ZoneDrawLayers`) :
 *  - emprise proposée d'après l'ordre, ou tracée et ajustée : fuseau prévu en tirets bleu ami à la largeur doctrinale,
 *    empreinte préparée en gris tireté (calculée par le jumeau du serveur, `preparedFootprint`), extrémités de l'axe ;
 *    puce de longueur et de surface pendant le tracé et l'ajustement ;
 *  - terrain en préparation : son empreinte en gris tireté (le contour du terrain prêt vient de ses couches) ;
 *  - repère pointé (coordonnées sans zone de grille) : anneau d'action.
 *  Le contour gris n'est pas une mesure tactique : pas de couleur APP-6. Entrée en fondu (200 ms). */

interface EngineParts {
  Source: ComponentType<any>;
  Layer: ComponentType<any>;
  Marker: ComponentType<any>;
}

const FADE_MS = 200;
const FOOTPRINT_GREY = "#8a909c";
const ACTION_BLUE = "#2f6fed"; // jeton accent
const EMPTY: FeatureCollection = { type: "FeatureCollection", features: [] };
const collection = (features: Feature[]): FeatureCollection => ({ type: "FeatureCollection", features });
const polygon = (ring: LonLat[], properties: Record<string, unknown> = {}): Feature => ({
  type: "Feature",
  geometry: { type: "Polygon", coordinates: [ring] },
  properties,
});
const line = (coordinates: LonLat[]): Feature => ({ type: "Feature", geometry: { type: "LineString", coordinates }, properties: {} });
const point = (at: LonLat): Feature => ({ type: "Feature", geometry: { type: "Point", coordinates: at }, properties: {} });

export interface TerrainView {
  /** Axe de l'emprise à préparer (proposé, tracé, ajusté en direct) ; null quand un terrain est lié. */
  axis: { start: LonLat; end: LonLat } | null;
  level: string | null;
  levelKey: string | null;
  /** Empreinte du terrain lié tant qu'il n'est pas prêt. */
  footprint: LonLat[] | null;
  anchor: LonLat | null;
  /** Premier point de l'axe posé, et position du pointeur (tracé en deux clics). */
  drawing: { start: LonLat | null; hover: LonLat | null } | null;
  /** Puce de longueur et de surface (tracé, ajustement). */
  tag: boolean;
}

export function VerificationTerrain({ Source, Layer, Marker, view }: EngineParts & { view: TerrainView }) {
  const shapes = useMemo(() => {
    const corridor: Feature[] = [];
    const footprint: Feature[] = [];
    const ends: Feature[] = [];
    let tag: { at: LonLat; text: string; refused: boolean } | null = null;
    const drawAxis = (start: LonLat, end: LonLat, live: boolean) => {
      const result = preparedFootprint(start, end, view.level, view.levelKey);
      if (isZoneLevel(view.level)) corridor.push(polygon(orientedRing(start, end, 0, corridorWidthM(view.level, view.levelKey) / 2)));
      corridor.push(line([start, end]));
      if (result.footprint) footprint.push(polygon(result.footprint.ring, { refused: !result.ok }));
      ends.push(point(start), point(end));
      if (live) tag = { at: end, ...axisTagText(result, view.level ?? "", null) };
    };
    if (view.drawing?.start && view.drawing.hover) drawAxis(view.drawing.start, view.drawing.hover, true);
    else if (view.drawing?.start) ends.push(point(view.drawing.start));
    else if (view.axis) drawAxis(view.axis.start, view.axis.end, view.tag);
    if (view.footprint) footprint.push(polygon(view.footprint));
    return { corridor: collection(corridor), footprint: collection(footprint), ends: collection(ends), tag };
  }, [view]);
  const key = view.footprint ? "zone" : view.axis || view.drawing ? "axis" : view.anchor ? "anchor" : null;
  const on = useFadeIn(key);
  const has = (c: FeatureCollection) => c.features.length > 0;
  const tag = shapes.tag as { at: LonLat; text: string; refused: boolean } | null;
  return (
    <>
      <Source id="gx-verif-footprint" type="geojson" data={has(shapes.footprint) ? shapes.footprint : EMPTY}>
        <Layer
          id="verif-footprint-fill"
          type="fill"
          paint={revealPaint("fill", { "fill-color": FOOTPRINT_GREY, "fill-opacity": ["case", ["==", ["get", "refused"], true], 0.12, 0.06] }, on, FADE_MS)}
        />
        <Layer
          id="verif-footprint-edge"
          type="line"
          paint={revealPaint("line", { "line-color": FOOTPRINT_GREY, "line-width": 1.25, "line-dasharray": [3, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-planned" type="geojson" data={has(shapes.corridor) ? shapes.corridor : EMPTY}>
        <Layer
          id="verif-planned"
          type="line"
          paint={revealPaint("line", { "line-color": FRIEND_BLUE, "line-width": 2, "line-dasharray": [2.5, 2] }, on, FADE_MS)}
        />
      </Source>
      <Source id="gx-verif-ends" type="geojson" data={has(shapes.ends) ? shapes.ends : EMPTY}>
        <Layer
          id="verif-ends"
          type="circle"
          paint={revealPaint(
            "circle",
            { "circle-radius": 5, "circle-color": "#ffffff", "circle-stroke-color": FRIEND_BLUE, "circle-stroke-width": 2 },
            on,
            FADE_MS,
          )}
        />
      </Source>
      <Source id="gx-verif-anchor" type="geojson" data={view.anchor ? collection([point(view.anchor)]) : EMPTY}>
        <Layer
          id="verif-anchor"
          type="circle"
          paint={revealPaint(
            "circle",
            { "circle-radius": 8, "circle-color": "#ffffff", "circle-opacity": 0.7, "circle-stroke-color": ACTION_BLUE, "circle-stroke-width": 3 },
            on,
            FADE_MS,
          )}
        />
      </Source>
      {tag && (
        <Marker longitude={tag.at[0]} latitude={tag.at[1]} anchor="bottom" offset={[0, -14]} style={{ pointerEvents: "none" }}>
          <div
            aria-hidden
            className={clsx(
              motion("zoom"),
              "pointer-events-none flex h-6 items-center rounded-md border px-2 font-sans whitespace-nowrap shadow-[0_1px_2px_rgb(16_18_24/0.14)]",
              tag.refused ? "border-warn/30 bg-warn-soft text-warn" : "border-line-strong bg-surface text-ink",
            )}
          >
            <span className="text-meta leading-none font-medium tabular-nums">{tag.text}</span>
          </div>
        </Marker>
      )}
    </>
  );
}

/** Libellé accessible des poignées de l'emprise. */
export const AXIS_HANDLE_LABEL = {
  start: () => t("Déplacer l'arrière de l'emprise"),
  end: () => t("Déplacer l'avant de l'emprise"),
  middle: () => t("Déplacer toute l'emprise"),
} as const;
